import { Outlet, Link, useNavigate } from 'react-router-dom';
import { LogOut, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuditorAuthStore } from '@/store/auditor-auth.store';
import { useAuditorLogout } from '@/api/hooks/useAuditorAccess';

function WatermarkBanner({ auditorDisplayName, auditFirmName, expiresAt }: {
  auditorDisplayName: string;
  auditFirmName: string;
  expiresAt: string;
}) {
  return (
    <div className="sticky top-0 z-30 flex items-center justify-between border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 shrink-0" />
        <span>
          Read-only audit view — <strong>{auditorDisplayName}</strong> ({auditFirmName}).
          {' '}Access expires {new Date(expiresAt).toLocaleDateString()}.
        </span>
      </div>
    </div>
  );
}

function AuditorNav() {
  const navigate = useNavigate();
  const logout = useAuditorLogout();

  async function handleLogout() {
    try {
      await logout.mutateAsync();
      navigate('/auditor/activate/', { replace: true });
    } catch {
      toast.error('Logout failed');
    }
  }

  return (
    <header className="flex items-center justify-between border-b border-border-subtle bg-bg-surface px-6 py-3">
      <Link to="/auditor/dashboard" className="flex items-center gap-2 font-semibold text-text-primary">
        <ShieldCheck className="h-5 w-5 text-accent-cyan" />
        Audit Review
      </Link>
      <div className="flex items-center gap-4">
        <Link to="/auditor/frameworks" className="text-sm text-text-secondary hover:text-text-primary">Frameworks</Link>
        <button
          onClick={handleLogout}
          className="flex items-center gap-1 text-sm text-text-tertiary hover:text-text-primary"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </header>
  );
}

export function AuditorShell() {
  const { session } = useAuditorAuthStore();

  if (!session) return null;

  return (
    <div className="flex min-h-screen flex-col bg-bg-base">
      <WatermarkBanner
        auditorDisplayName={session.auditorDisplayName}
        auditFirmName={session.auditFirmName}
        expiresAt={session.expiresAt}
      />
      <AuditorNav />
      <main className="flex-1 px-6 py-6 max-w-6xl mx-auto w-full">
        <Outlet />
      </main>
    </div>
  );
}
